import type { ApiError } from '@approvalmax/types';
import { openSplitViewState } from '@approvalmax/ui/src/components';
import { browserHelpers, hooks, miscHelpers, routerHelpers } from '@approvalmax/utils';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { State } from 'modules/data';
import { mergeDeep } from 'modules/immutable';
import { hooks as pageHooks } from 'modules/page';
import { actions as profileActions } from 'modules/profile';
import { StartTrialPopupOrigin } from 'modules/profile/actions';
import { useSelector } from 'modules/react-redux';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { routingService } from 'services/routing';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';
import { getDefaultPath, getPath, Path } from 'urlBuilder';

import { loadOneRequest, loadPageData, loadRequests, setActiveRequestId } from '../actions';
import { MY_REQUESTS_LIST_PAGE_ID, QueryKeys, RequestListFilter } from '../config';
import { checkRequestLoaded } from '../selectors/pageSelectors';
import { MyRequestListPageData } from './useMyRequestsListPageData.types';

const useMyRequestsListPageData = () => {
    const dispatch = useDispatch();

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const setContentSplitView = useSetAtom(contentSplitViewState);

    const { section, companyId } = useParams<{ section: string; companyId?: string }>();

    const company = useSelector((state: State) => selectors.company.findCompanyById(state, companyId || ''));

    const location = useLocation();
    const history = useHistory();

    const activeRequestId = routerHelpers.getParamAsString(location.search, 'requestId', '');
    const attachmentId = routerHelpers.getParamAsString(location.search, 'attachmentId', '');
    const decision = routerHelpers.getParamAsString(location.search, 'decision', '');
    const requestVersion = routerHelpers.getParamAsString(location.search, 'requestVersion', '');
    const allFeaturesTrial = routerHelpers.getParamAsString(location.search, 'allFeaturesTrial', '');

    const [showStartTrialPopupCompanyId, , clearShowStartTrialPopupForCompanyId] = hooks.useQueryParam(
        'showStartTrialPopupForCompanyId'
    );
    const [startTrialPopupOrigin, , clearStartTrialPopupOrigin] =
        hooks.useQueryParam<StartTrialPopupOrigin>('startTrialPopupOrigin');

    const companyForStartTrialPopup = useSelector((state: State) =>
        selectors.company.findCompanyById(state, showStartTrialPopupCompanyId || '')
    );

    const isMobile = browserHelpers.isMobile();

    const firstRender = useRef(true);

    const isRequestLoaded = useSelector((state: State) => checkRequestLoaded(state, activeRequestId));

    const filter = useMemo(
        () => (miscHelpers.isEnumValue(RequestListFilter, section) ? section : RequestListFilter.MyDecisionRequired),
        [section]
    );

    const noCompanies = pageHooks.useNoCompanies();

    const awaitingActivationTrialCompanies = useSelector(selectors.company.getAwaitingActivationTrialCompanies);

    useEffect(() => {
        if (isMobile) {
            history.push({
                pathname: getPath(Path.mobileDecision),
                search: location.search,
            });
        }
    }, [history, isMobile, location.search]);

    useEffect(() => {
        if (!miscHelpers.isEnumValue(RequestListFilter, section)) {
            routingService.push(getDefaultPath());
        }
    }, [section]);

    const { isInitialLoading, isError, error } = useQuery<MyRequestListPageData, ApiError>(
        [QueryKeys.CompanyRequestsListPage, filter],
        async () => {
            if (companyId && !company) {
                // TODO: remove this if backend starts to throw error for wrong companyId
                throw Error('Company not found');
            }

            const hasAwaitingActivationTrialCompanies = allFeaturesTrial && awaitingActivationTrialCompanies.length > 0;

            if (hasAwaitingActivationTrialCompanies) {
                dispatch(
                    profileActions.openConsentToStartTrialPopup(awaitingActivationTrialCompanies[0], 'direct_link')
                );
            }

            if (companyForStartTrialPopup && !hasAwaitingActivationTrialCompanies) {
                dispatch(
                    profileActions.openConsentToStartTrialPopup(
                        companyForStartTrialPopup,
                        startTrialPopupOrigin || 'billing: land on all feature trial paywall'
                    )
                );

                clearShowStartTrialPopupForCompanyId();
                clearStartTrialPopupOrigin();
            }

            const requestsResponse = await loadRequests({ companyId: null, templateId: null, filter });
            const requestFromRequestsResponse = requestsResponse?.entities?.requests?.[activeRequestId];

            const oneRequestResponse = await loadOneRequest(
                firstRender.current && !requestFromRequestsResponse ? activeRequestId || '' : '',
                companyId
            );

            if (firstRender.current && requestFromRequestsResponse) {
                if (companyId && requestFromRequestsResponse.companyId !== companyId) {
                    throw Error('Request not found');
                }
            }

            if (firstRender.current && oneRequestResponse) {
                const { entities, request } = oneRequestResponse;

                if (companyId && request.companyId !== companyId) {
                    throw Error('Request not found');
                }

                if (!requestsResponse.requests.includes(request.id)) {
                    requestsResponse.requests = [request.id, ...requestsResponse.requests];
                    requestsResponse.entities = mergeDeep(requestsResponse.entities, entities);
                }
            }

            const firstRequestId = requestsResponse.requests[0];
            const requestId = activeRequestId || firstRequestId;

            firstRender.current = false;

            return {
                ...requestsResponse,
                requestId,
                activeRequestId,
                attachmentId,
                companyId:
                    companyId ||
                    oneRequestResponse?.request.companyId ||
                    requestsResponse.entities.requests?.[firstRequestId]?.companyId,
                decision,
                requestVersion,
            };
        },
        {
            enabled: !isMobile && !noCompanies,
            onSuccess: (data) => {
                if (!isMobile) {
                    dispatch(
                        loadPageData({
                            filter,
                            companyId: null,
                            templateId: null,
                            requestId: data.requestId,
                            requests: data.requests,
                            continuationToken: data.continuationToken,
                            hasMore: data.hasMore,
                            entities: data.entities,
                            innerPageId: MY_REQUESTS_LIST_PAGE_ID,
                        })
                    );

                    if (data.attachmentId) {
                        showSplitView(data.attachmentId);
                    }

                    navigateToRequest(data);
                }
            },
        }
    );

    const navigateToRequest = (data: MyRequestListPageData) => {
        if (data.companyId) {
            routingService.push(
                getPath(Path.requestMyListWithCompany, filter, data.companyId, {
                    requestId: data.requestId,
                    decision: data.decision,
                    requestVersion: data.requestVersion,
                })
            );
        } else {
            routingService.push(
                getPath(Path.requestMyList, filter, {
                    requestId: data.requestId,
                    decision: data.decision,
                    requestVersion: data.requestVersion,
                })
            );
        }
    };

    const showSplitView = (attachmentId: string) => {
        setOpenSplitView(true);
        setContentSplitView({
            mode: ContentSplitViewMode.AttachmentsToUnknown,
            attachmentId,
        });
    };

    useEffect(() => {
        if (isRequestLoaded) {
            if (firstRender.current || isMobile) {
                firstRender.current = false;

                return;
            }

            dispatch(setActiveRequestId(activeRequestId));
        }
    }, [activeRequestId, dispatch, isMobile, isRequestLoaded]);

    return { isInitialLoading, filter, isError, error };
};

export default useMyRequestsListPageData;
